/**
 * You and your crewmates: round figures with a body, a head and two eyes, instanced per body part so
 * twenty crewmates cost three draw calls. Instance 0 is you. They cast real shadows in the classic
 * look; in the stylized one, without a shadow map, each stands in a soft dark spot instead.
 */
import * as THREE from 'three';

import { radialTexture } from '../stage/scene';

export const MAX_FIGURES = 24;

export interface Figures {
  /** Place figure `index` at `position` (world units, in the parent's space), facing `quaternion`. */
  set(
    index: number,
    position: THREE.Vector3,
    quaternion: THREE.Quaternion,
    bob: number,
    color: THREE.Color,
  ): void;
  /** How many figures are shown. */
  setCount(count: number): void;
  commit(): void;
}

const _m = new THREE.Matrix4();
const _p = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _s = new THREE.Vector3(1, 1, 1);
const _eye = new THREE.Vector3();
const _eyeS = new THREE.Vector3(1, 1.25, 0.6);

/** How figures meet the ground: real shadows, or a soft spot under each (no shadow map). */
export type FigureShadows = 'cast' | 'spot';

/** A soft round shadow lying on the ground, as wide as a figure's shoulders and then some. */
function shadowSpots(): THREE.InstancedMesh {
  const spots = new THREE.InstancedMesh(
    new THREE.CircleGeometry(0.26, 20).rotateX(-Math.PI / 2),
    new THREE.MeshBasicMaterial({
      map: radialTexture([
        [0, 'rgba(24,30,42,.42)'],
        [0.55, 'rgba(24,30,42,.2)'],
        [1, 'rgba(24,30,42,0)'],
      ]),
      transparent: true,
      depthWrite: false,
    }),
    MAX_FIGURES,
  );
  spots.frustumCulled = false;
  spots.count = 0;
  return spots;
}

export function createFigures(parent: THREE.Object3D, shadows: FigureShadows = 'cast'): Figures {
  const skin = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.45, metalness: 0 });
  const eyeMaterial = new THREE.MeshStandardMaterial({ color: 0x22252b, roughness: 0.3 });
  const body = new THREE.InstancedMesh(
    new THREE.CapsuleGeometry(0.16, 0.2, 10, 20),
    skin,
    MAX_FIGURES,
  );
  const head = new THREE.InstancedMesh(new THREE.SphereGeometry(0.16, 28, 20), skin, MAX_FIGURES);
  const eyes = new THREE.InstancedMesh(
    new THREE.SphereGeometry(0.026, 12, 8),
    eyeMaterial,
    MAX_FIGURES * 2,
  );
  for (const mesh of [body, head]) {
    mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(MAX_FIGURES * 3), 3);
    mesh.castShadow = shadows === 'cast';
    mesh.frustumCulled = false;
    mesh.count = 0;
  }
  eyes.frustumCulled = false;
  eyes.count = 0;
  parent.add(body, head, eyes);
  const spots = shadows === 'spot' ? shadowSpots() : null;
  if (spots) parent.add(spots);

  return {
    set(index, position, quaternion, bob, color) {
      // body, 0.3 up (plus the walking bob), head 0.65 up, eyes in front of the head
      _p.set(0, 0.3 + bob, 0)
        .applyQuaternion(quaternion)
        .add(position);
      _m.compose(_p, quaternion, _s);
      body.setMatrixAt(index, _m);
      body.setColorAt(index, color);
      if (spots) {
        // just above the ground, so it never flickers into the tile
        _p.set(0, 0.012, 0).applyQuaternion(quaternion).add(position);
        _m.compose(_p, quaternion, _s);
        spots.setMatrixAt(index, _m);
      }
      _p.set(0, 0.65, 0).applyQuaternion(quaternion).add(position);
      _m.compose(_p, quaternion, _s);
      head.setMatrixAt(index, _m);
      head.setColorAt(index, color);
      for (let e = 0; e < 2; e++) {
        _eye
          .set(e === 0 ? -0.06 : 0.06, 0.68, 0.145)
          .applyQuaternion(quaternion)
          .add(position);
        _q.copy(quaternion);
        _m.compose(_eye, _q, _eyeS);
        eyes.setMatrixAt(index * 2 + e, _m);
      }
    },
    setCount(count) {
      body.count = count;
      head.count = count;
      eyes.count = count * 2;
      if (spots) spots.count = count;
    },
    commit() {
      body.instanceMatrix.needsUpdate = true;
      head.instanceMatrix.needsUpdate = true;
      eyes.instanceMatrix.needsUpdate = true;
      if (spots) spots.instanceMatrix.needsUpdate = true;
      if (body.instanceColor) body.instanceColor.needsUpdate = true;
      if (head.instanceColor) head.instanceColor.needsUpdate = true;
    },
  };
}
